import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { toSnakeCase, withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { i18n, log } from '~/utils';

import { DATA_EXCHANGE_STATUS } from './constants';
import { queryKeysCompany } from './queryKeys';
import { zodCompanyAdminSchema } from './validations';

const zodCreateCompanyEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('company'),
]);

export const zodCompanyDataSchema = z.object({
  address: z
    .object({
      buildingNumber: z.string().optional(),
      city: z.string().optional(),
      country: z.string().optional(),
      postCode: z.string().optional(),
      streetName: z.string().optional(),
    })
    .optional(),
  alertConfig: z
    .object({
      numDays: z.number().int().nonnegative().optional(),
      onlyWorkDays: z.boolean().optional(),
      delayedDays: z.number().int().nonnegative().optional(),
    })
    .optional(),
  blacklisted: z.boolean().optional(),
  companyAccountId: z.uuid().optional(),
  currentIssues: z.string().optional(),
  furtherInformation: z.string().optional(),
  gln: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  hra: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  hraRegisterCourt: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  hrb: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  hubspotDealId: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  interchangeGln: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  isActive: z.boolean().optional(),
  isReceiver: z.boolean().optional(),
  isSender: z.boolean().optional(),
  knownIssues: z.string().optional(),
  name: z.string().trim().min(1),
  rank: z.number().optional(),
  registerCourt: z.string().trim().nullish(), // nullish to allow sending an explicit null to clear the value
  statusDln: z
    .preprocess(
      (value) => toSnakeCase(String(value)),
      z.enum(DATA_EXCHANGE_STATUS),
    )
    .optional(),
  statusInvoice: z
    .preprocess(
      (value) => toSnakeCase(String(value)),
      z.enum(DATA_EXCHANGE_STATUS),
    )
    .optional(),
  syncKey: z.string().trim().nullish(),
  // nullish so updates can send an explicit null to clear the VAT id
  vatId: z.string().trim().nullish(),
});

export type CompanyData = z.infer<typeof zodCompanyDataSchema>;

/**
 * What the create/update endpoints return (`CompanyInternalResponse` in the
 * OpenAPI spec) — the same shape the single-company GET endpoint returns, so
 * this reuses that schema rather than re-deriving it from the request shape.
 *
 * @see ./validations.ts
 */
export const zodCompanyResponseSchema = zodCompanyAdminSchema;

const zodCreateCompanyResponseSchema = zodCompanyResponseSchema;

type CreateCompanyResponse = z.infer<typeof zodCreateCompanyResponseSchema>;

/**
 * Creates a new company as Admin.
 *
 * @param companyData - An object containing the company details to be created.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/create_company_vadmin_company_post
 */
// fallow-ignore-next-line unused-export
export const createCompanyAdmin = async (companyData: CompanyData) => {
  try {
    const sanitizedCompanyData = withoutObjectKeysWhereValueIs(companyData, [
      null,
      undefined,
      '',
    ]);

    return await vestigasApi
      .post(ENDPOINT.COMPANY.CREATE(), {
        json: sanitizedCompanyData,
        validate: {
          body: zodCompanyDataSchema,
          pathSegments: zodCreateCompanyEndpointSchema,
          response: zodCreateCompanyResponseSchema,
        },
      })
      .json<CreateCompanyResponse>();
  } catch (error) {
    log.error({
      message: 'Error creating company',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 409: {
          errorMessage = i18n.t(($) => $.company.createCompany.error409, {
            ns: 'data',
          });
          break;
        }

        default: {
          errorMessage = i18n.t(($) => $.company.createCompany.error.generic, {
            ns: 'data',
          });
        }
      }
    }

    if (errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * Custom hook for creating a company as Admin using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Company/operation/create_company_vadmin_company_post
 */
export const useMutationCreateCompanyAdmin = (
  options?: Omit<
    UseMutationOptions<CreateCompanyResponse, Error, CompanyData>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<CreateCompanyResponse, Error, CompanyData>({
    mutationFn: createCompanyAdmin,
    onSuccess(data) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAllAdmin({}),
      });

      return data;
    },
    ...options,
  });
};
